/**
 * The communication API client — templates, bindings, reminder rules, and what
 * went out. Spec §K.
 *
 * **Nothing here is a message and nothing here is an address.** A delivery row
 * carries ids, a template code and a status; the address is on the client
 * record, where a firm edits it, and the message body is core-communication's
 * (`services/prodesk-service/app/models/comms.py`). A screen that wanted to
 * show "we emailed books@acme.example" would be asking for a column this app
 * deliberately does not have.
 *
 * **Two of the five audiences cannot be addressed.** `assignee` and `reviewer`
 * are platform user ids and ProDesk holds no address for a member of the firm —
 * there is no member directory (spec §9-20) and core's `/internal/send` requires
 * a `to_address`. The vocabulary says so per audience (`addressable`), and the
 * screen says it **before** a firm saves a binding rather than after it fails to
 * send. Spec §9-24.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

/** FR-K3's five recipients, exactly. */
export type Audience = 'client' | 'assignee' | 'reviewer' | 'manager' | 'lead'

/** FR-K4's three rule kinds. Call-back reminders are FR-K4's fourth and were
 *  shipped by Wave 4A on the call back itself — not re-implemented here. */
export type RuleKind = 'due_date' | 'document_request' | 'invoice'

export type ReminderStatus =
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'skipped'
  | 'failed'
  | 'cancelled'

/** `sending` is not a flicker. It is committed **before** the call to core, and
 *  a row left in it by a crashed worker is never retried: the send contract has
 *  no idempotency key, so nobody knows whether that message went out. */
export type DeliveryStatus = 'pending' | 'posted' | 'sent' | 'skipped' | 'failed'

export interface TemplateRecord {
  id: string
  /** What a caller inside ProDesk asks for. Three waves shipped three
   *  conventions — `ca-erp.person.invite`, `prodesk.lead.acknowledgement` and
   *  the bare `case_opened` — and none is renamed. */
  key: string
  /** What core-communication is called with. */
  code: string
  name: string
  channel: string | null
  subject: string | null
  /** The firm's own words. **Not what goes out** — core sends its registered
   *  template; this is what the firm edits and what the platform registration
   *  ask carries. */
  body: string
  variables: string[]
  category: string | null
  description: string | null
  source: 'shipped' | 'firm'
  active: boolean
  edited_at: string | null
  created_at: string
}

export interface BindingRecord {
  id: string
  event: string
  event_label: string
  template_id: string
  template_key: string | null
  template_name: string | null
  audience: Audience
  channel: string | null
  active: boolean
  /** Why a shipped binding is off. Three of them are off because the module
   *  that publishes the event already sends that message itself. */
  note: string | null
}

export interface RuleRecord {
  id: string
  name: string
  kind: RuleKind
  template_id: string
  template_key: string | null
  template_name: string | null
  audience: Audience
  channel: string | null
  /** Days relative to the due date. **Negative is before.** */
  offsets: number[]
  interval_days: number | null
  max_count: number | null
  active: boolean
}

export interface ReminderRecord {
  id: string
  rule_id: string
  client_id: string
  subject_kind: 'case' | 'document_request' | 'invoice'
  subject_id: string
  case_id: string | null
  audience: Audience
  send_at: string
  due_on: string | null
  sequence: number
  offset_days: number | null
  status: ReminderStatus
  sent_at: string | null
  attempts: number
  reason: string | null
  delivery_id: string | null
}

export interface DeliveryRecord {
  id: string
  source_kind: 'binding' | 'reminder' | 'manual'
  source_id: string | null
  event: string | null
  template_code: string
  audience: Audience | null
  client_id: string | null
  case_id: string | null
  lead_id: string | null
  /** A **staff** recipient we had no address for. Present on the `skipped` row
   *  that says so — spec §9-24. */
  user_id: string | null
  channel: string | null
  status: DeliveryStatus
  reason: string | null
  provider_message_id: string | null
  sent_at: string | null
  created_at: string
}

export interface AudienceOption {
  code: Audience
  label: string
  addressable: boolean
  note?: string
}

export interface EventOption {
  key: string
  label: string
  audiences: Audience[]
  variables: string[]
}

export interface Vocabulary {
  audiences: AudienceOption[]
  events: EventOption[]
  channels: string[]
  rule_kinds: RuleKind[]
  reminder_statuses: ReminderStatus[]
  reasons: string[]
  defaults_version: string
  /** What the platform is being asked to register in core-communication, in
   *  core's own `POST /api/v1/admin/templates` shape. Until it is granted every
   *  one of these codes answers 404 and nothing sends. */
  registration_ask: Array<Record<string, unknown>>
}

// -- the client --------------------------------------------------------------

export const comms = {
  vocabulary: () => api<Vocabulary>('/api/v1/comms/vocabulary'),

  templates: () =>
    api<{ templates: TemplateRecord[] }>('/api/v1/comms/templates').then(
      (r) => r.data.templates,
    ),
  createTemplate: (body: Partial<TemplateRecord> & { key: string; name: string }) =>
    api<TemplateRecord>('/api/v1/comms/templates', { method: 'POST', body }),
  updateTemplate: (id: string, body: Partial<TemplateRecord>) =>
    api<TemplateRecord>(`/api/v1/comms/templates/${id}`, { method: 'PATCH', body }),
  resetTemplate: (id: string) =>
    api<TemplateRecord>(`/api/v1/comms/templates/${id}/reset`, { method: 'POST' }),
  /** Sends to the **firm's own recorded address**, never to one typed into a
   *  box — an app that posts to any address a signed-in user supplies is an
   *  open relay with a login screen on it. */
  sendTest: (id: string) =>
    api<{ sent: boolean; delivery: DeliveryRecord }>(
      `/api/v1/comms/templates/${id}/send-test`,
      { method: 'POST' },
    ),

  bindings: () =>
    api<{ bindings: BindingRecord[] }>('/api/v1/comms/bindings').then((r) => r.data.bindings),
  createBinding: (body: {
    event: string
    template_id: string
    audience: Audience
    channel?: string | null
    active?: boolean
  }) => api<BindingRecord>('/api/v1/comms/bindings', { method: 'POST', body }),
  updateBinding: (id: string, body: Partial<BindingRecord>) =>
    api<BindingRecord>(`/api/v1/comms/bindings/${id}`, { method: 'PATCH', body }),
  deleteBinding: (id: string) =>
    api<{ deleted: string }>(`/api/v1/comms/bindings/${id}`, { method: 'DELETE' }),

  rules: () => api<{ rules: RuleRecord[] }>('/api/v1/comms/rules').then((r) => r.data.rules),
  createRule: (body: {
    name: string
    kind: RuleKind
    template_id: string
    audience?: Audience
    offsets?: number[]
    interval_days?: number | null
    max_count?: number | null
    active?: boolean
  }) => api<RuleRecord>('/api/v1/comms/rules', { method: 'POST', body }),
  updateRule: (id: string, body: Partial<RuleRecord>) =>
    api<RuleRecord>(`/api/v1/comms/rules/${id}`, { method: 'PATCH', body }),

  reminders: (params: { status?: ReminderStatus | ''; limit?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<{
      reminders: ReminderRecord[]
      counts: Partial<Record<ReminderStatus, number>>
      /** Rows a worker claimed and never finished. Never retried — see
       *  `ReminderStatus`. Shown so a person can decide. */
      stuck: ReminderRecord[]
    }>(`/api/v1/comms/reminders${suffix}`)
  },
  /** The same function the 02:00 job runs. It does **not** send anything:
   *  materialising and sending are two things. */
  materialise: () =>
    api<{ created: number; cancelled: number; rules: number }>(
      '/api/v1/comms/reminders/materialise',
      { method: 'POST' },
    ),
  cancelReminder: (id: string) =>
    api<ReminderRecord>(`/api/v1/comms/reminders/${id}/cancel`, { method: 'POST' }),

  deliveries: (params: { status?: DeliveryStatus | ''; limit?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<{
      deliveries: DeliveryRecord[]
      counts: Partial<Record<DeliveryStatus, number>>
    }>(`/api/v1/comms/deliveries${suffix}`)
  },
}

/**
 * A reason code in the firm's language.
 *
 * The service's reasons are engineer's words on a row, deliberately (they are
 * a closed set so this function can exist). Two of them are not the firm's
 * fault and should not read as though they were.
 */
export function explainReason(reason: string | null): string {
  switch (reason) {
    case 'no_address':
      return 'No email address or mobile number on the client record.'
    case 'no_staff_address':
      return 'ProDesk holds no address for a member of the firm.'
    case 'no_firm_address':
      return 'The firm has no email address in its own profile.'
    case 'stage_not_client_visible':
      return 'Suppressed — the case was on a stage the client does not see.'
    case 'no_recipient':
      return 'There was nobody to send it to.'
    case 'template_inactive':
      return 'The template is switched off.'
    case 'not_delivered':
      return 'core-communication did not accept it. The template may not be registered yet.'
    case 'subject_resolved':
      return 'No longer needed — it had already been dealt with.'
    case 'cancelled_by_firm':
      return 'Cancelled.'
    case null:
      return ''
    default:
      return reason
  }
}

/** `-7` → "7 days before", `0` → "on the day", `3` → "3 days after". */
export function describeOffset(days: number): string {
  if (days === 0) return 'on the day'
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} before`
  return `${days} day${days === 1 ? '' : 's'} after`
}
